import React, { useState } from "react";
import getConfig from "next/config";
import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import { UseComboboxStateChange } from "downshift"; // eslint-disable-line import/no-extraneous-dependencies

import {
    PageLayout, IndexedObject, Search, ComposableHeaderDropdownProviderProps, Header
} from "@themuse/design-system";
import {
    initSnowplowTracker,
    trackSnowplowImpressions,
    triggerSnowplowPageViewEvent,
    trackSnowplowClicks,
    trackSnowplowModalActions
} from "@themuse/snowplow-js-client";
import {
    Experiment,
    ExperimentProvider,
    getExperiments,
} from "@themuse/experiment-client";

import { UserActionTypes, PageMetaDataActionTypes } from "interfaces/context/appContextInterfaces";
import { userTrack, pushDataLayerEvent } from "utils/tracking";
import { isBrowser } from "utils/browser";
import { getActiveFeatureFlags } from "utils/featureFlags";
import { formatLog } from "utils/logging";
import { getCanonicalUrl } from "utils/strings";
import {
    AppArticle,
    getCompressedArticle
} from "utils/orm";
import {
    fetchUser, getOptimizedPosts, ApiArticle, CompanySuggestion, fetchCompanySuggestions, fetchApiCarmenData
} from "utils/api";

import { AppProvider, useAppState, useAppDispatch } from "hooks/AppContext";
import { useSessionStorage } from "hooks/useSessionStorage";
import { FeatureFlagsProvider } from "hooks/FeatureFlagsContext";
import { AppPageLayout } from "components/AppPageLayout";
import { ArticleContainer } from "components/ArticleContainer";
import { SubNav } from "components/SubNav";
import { SEO } from "components/SEO";

import styles from "styles/Home.module.scss";

interface InitialAppParams {
    article: AppArticle;
    path: string;
}

/**
 * A config for the experiments.
 * @example
 * {
 *     name: "EXPERIMENT_KEY",
 *     variants: [{
 *         name: "control",
 *         weight: 50
 *     }, {
 *         name: "enabled",
 *         weight: 50
 *     }]
 * }
 */
const experimentConfig: Experiment[] = [
    // no active experiment
];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getServerSideProps = async (context: GetServerSidePropsContext) => {
    let initialAppParams: InitialAppParams = {
        article: null,
        path: ""
    };

    // This article can never exist.
    // https://nextjs.org/docs/api-reference/data-fetching/get-server-side-props#notfound
    if (
        context.req.url === "/advice/null"
        || (/\/advice\/[^\w]/i).test(context.req.url)
        || (/^http/i).test(context.req.url)
    ) {
        return {
            notFound: true
        };
    }

    initialAppParams = {
        ...initialAppParams,
        ...await setInitialAppParams(context)
    };

    // We received a redirect from the CMS so this is a 308.
    // https://nextjs.org/docs/api-reference/next.config.js/redirects
    if (initialAppParams.article.meta?.status === "redirect") {
        return {
            redirect: {
                destination: initialAppParams.article.meta?.location,
                permanent: true,
            },
        };
    }

    return {
        // This article does not exist in the CMS so this is a 404.
        // https://nextjs.org/docs/api-reference/data-fetching/get-server-side-props#notfound
        notFound: !initialAppParams?.article?.id,
        props: {
            ...initialAppParams,
            experiments: getExperiments(experimentConfig, context),
            featureFlags: {
                ...getActiveFeatureFlags(context),
            }
        }
    };
};

const setInitialAppParams = async (context: GetServerSidePropsContext): Promise<InitialAppParams> => { // eslint-disable-line
    const { articleUri, token, "x-craft-live-preview": preview } = context?.query;
    let apiArticle: ApiArticle = null;
    try {
        const uri = `advice/${articleUri}`;
        const response = await getOptimizedPosts({ uri, token, preview });
        const { meta, data } = response;
        const { type } = meta || {};

        apiArticle = {
            ...data,
            meta,
            templateName: type === "listicles" ? "listicle" : "article"
        };
    } catch (error) {
        const msg = `Article Renderer was unable to retrieve the post advice/${articleUri}`;
        console.warn(msg, JSON.stringify(formatLog({ context, error })));
    }

    const article = await getCompressedArticle(apiArticle);

    return {
        article,
        path: context.resolvedUrl
    };
};

const setUserCarmenData = async (setUserLocation: any) => {
    try {
        const carmenResponse = await fetchApiCarmenData();
        if (carmenResponse) {
            setUserLocation(JSON.stringify(carmenResponse));
        }
    } catch (error) {
        const msg = `${process.env.APP_NAME} was unable to load location from Carmen.`;
        console.error(msg, JSON.stringify(formatLog({ error })));
    }
};

const setUser = async (userLocation, dispatch) => {
    try {
        const apiUser = await fetchUser();
        const userData = userLocation ? {
            location: JSON.parse(userLocation)
        } : {};
        dispatch({
            type: UserActionTypes.updateUser,
            payload: {
                ...(apiUser?.logged_in && apiUser),
                ...userData,
                isInitialized: true,
            }
        });
    } catch (error) {
        const msg = `${process.env.APP_NAME} was unable to request the user.`;
        console.error(msg, JSON.stringify(formatLog({ error })));
    }
};

const UserInit: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const { user } = useAppState();
    const [userLocation, setUserLocation] = useSessionStorage("user_location");
    const appDispatch = useAppDispatch();

    React.useEffect(() => {
        setUser(userLocation, appDispatch);
        // NOTE: An email with `themuse.com` does not mean the user is an admin
        const isMuser = user?.email && user.email.split("@")[1] === "themuse.com";
        userTrack({
            signed_in: user.logged_in ?? false,
            is_muser: isMuser,
            is_admin: isMuser,
            user_id: user.id ?? null
        });
    }, [userLocation]);

    React.useEffect(() => {
        setUserCarmenData(setUserLocation);
    }, []);

    React.useEffect(() => {
        appDispatch({
            type: UserActionTypes.updateUser,
            payload: {
                ...user,
                location: userLocation ? JSON.parse(userLocation) : user?.location
            }
        });
    }, [userLocation]);

    React.useEffect(() => {
        // NOTE: An email with `themuse.com` does not mean the user is an admin
        const isMuser = user?.email && user.email.split("@")[1] === "themuse.com";
        userTrack({
            signed_in: user?.logged_in ?? false,
            is_muser: isMuser,
            is_admin: isMuser,
            user_id: user?.id ?? null
        });
    }, [user]);

    return <>{ children }</>;
};

const PageMetaInit: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const { pageMetaData } = useAppState();
    const appDispatch = useAppDispatch();

    React.useEffect(() => {
        const { href, search } = window.location;
        const { referrer } = document;
        appDispatch({
            type: PageMetaDataActionTypes.updatePageMetaData,
            payload: {
                ...pageMetaData,
                ...{
                    href,
                    search,
                    referrer
                }
            }
        });
    }, []);

    return <>{ children }</>;
};

const SnowPlowInit = () => {
    const { publicRuntimeConfig: { snowplowCollector } } = getConfig();
    const { user, article: { sponsor, id: articleId } } = useAppState();
    const [pageViewTriggered, setPageViewTriggered] = useState(false);
    const { legacyId: companySponsorId } = sponsor;
    React.useEffect(() => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            sp_page_is_external: false,
            sp_page_section: "advice",
            sp_page_sponsor_id: companySponsorId?.toString() || null,
            sp_page_tab: null,
            sp_page_type: "content",
            sp_page_entity_id: articleId.toString() || null,
            sp_page_entity_uuid: null,
            sp_user_logged_in: user?.logged_in,
            sp_user_id: user?.id,
            sp_experiment_experiment_id: "",
            sp_experiment_variant_id: ""
        });
        initSnowplowTracker(snowplowCollector);
        trackSnowplowImpressions();
        trackSnowplowClicks();
        trackSnowplowModalActions();
    }, []);

    React.useEffect(() => {
        if (user.isInitialized && !pageViewTriggered) {
            triggerSnowplowPageViewEvent();
            setPageViewTriggered(true);
        }
    }, [user]);

    return null;
};

export const formatCompanySuggestions = (companySuggestions: CompanySuggestion[]) => companySuggestions.slice(0, 4).map((companyResult: CompanySuggestion) => {
    const { text, value } = companyResult;
    const { short_name } = value; // eslint-disable-line camelcase
    return {
        label: `${text}`,
        value: `/profiles/${short_name}` // eslint-disable-line camelcase
    };
});

export const updateInput = async (
    changes: UseComboboxStateChange<IndexedObject>,
    setItems: (items: IndexedObject[]) => void,
    setInputValue: (inputValue: string) => void,
    fetchData:(inputValue: string) => Promise<CompanySuggestion[]>
) => {
    const { inputValue } = changes;
    const jobSearchItem = {
        label: `${inputValue}`,
        value: `/search/keyword/${inputValue}/`,
    };
    setInputValue(inputValue);
    if (inputValue) {
        setItems([jobSearchItem]);
        const companyResults = await fetchData(inputValue);
        const companyList = companyResults?.length ? formatCompanySuggestions(companyResults) : [];
        setItems([jobSearchItem].concat(companyList));
    } else {
        setItems([]);
    }
};

const Home = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const {
        article, path, featureFlags, experiments
    } = props;
    const {
        articleUri,
        contentAuthor,
        breadcrumbLinks,
        dateUpdated,
        excerpt,
        leadMedia,
        postDate,
        primaryTopic,
        seo,
        title
    } = article;
    const {
        writerName,
        writerUri,
    } = contentAuthor ?? {};

    const canonical = getCanonicalUrl(path);
    const description = excerpt;
    const pageMetaData = {
        pathName: path
    };
    const [dropdownItems, setDropdownItems] = useState([] as IndexedObject[]);
    const [inputValue, setInputValue] = useState("");

    const searchQuerySubmittedEvent = (selectedItemText: string) => {
        const searchTerm = selectedItemText || inputValue;
        const eventProps = {
            search_term: searchTerm || ""
        };
        pushDataLayerEvent("searchQuerySubmitted", eventProps);
    };

    const onKeyboardSelectItem = (_: React.KeyboardEvent<HTMLInputElement>, selectedItem: IndexedObject) => {
        if (!isBrowser()) return "";

        const searchPath = inputValue
            ? `/search/keyword/${inputValue}`
            : "/search";
        const selectPath = selectedItem?.value
            ? selectedItem?.value
            : searchPath;

        searchQuerySubmittedEvent(selectedItem?.label);

        return window.open(selectPath, "_blank").focus();
    };

    const onMouseSelectItem = (_: React.MouseEvent<HTMLInputElement>, selectedItem: IndexedObject) => {
        searchQuerySubmittedEvent(selectedItem.label);
    };

    const dropdownProps: ComposableHeaderDropdownProviderProps = {
        IconComponent: <Search />,
        debounceWait: 10,
        dropdownList: dropdownItems,
        inputId: "search-input",
        onInputChange: (changes: UseComboboxStateChange<IndexedObject>) => updateInput(
            changes,
            setDropdownItems,
            setInputValue,
            fetchCompanySuggestions
        ),
        noResultsMessage: " ",
        onKeyboardSelectItem,
        onMouseSelectItem
    };

    const pageLayoutProps: React.ComponentProps<typeof PageLayout> & React.PropsWithChildren<React.ComponentProps<typeof Header>> = {
        activePath: "/advice",
        headerClassName: styles.pageHeader,
        pageName: styles.pageWrapper,
        mainClassName: styles.main,
        enableCompanySearch: true,
        companySearchDropdownProps: dropdownProps,
        showSubMenus: true
    };

    return (
        <ExperimentProvider experiments={experiments}>
            <FeatureFlagsProvider featureFlags={featureFlags}>
                <AppProvider {...{ article, pageMetaData }}>
                    <SEO {...{
                        articleUri,
                        canonical,
                        breadcrumbLinks,
                        dateUpdated,
                        description,
                        leadMedia,
                        postDate,
                        primaryTopic,
                        seo,
                        title,
                        writerName,
                        writerUri,
                    }}
                    />
                    <UserInit>
                        <PageMetaInit>
                            <SnowPlowInit />
                            <AppPageLayout {...pageLayoutProps}>
                                <SubNav />
                                <ArticleContainer />
                            </AppPageLayout>
                        </PageMetaInit>
                    </UserInit>
                </AppProvider>
            </FeatureFlagsProvider>
        </ExperimentProvider>
    );
};

export default Home;
